import { api } from '@/lib/api';

export interface AgingRow {
  name: string;
  current: number;
  days1to30: number;
  days31to60: number;
  days61to90: number;
  days91plus: number;
  total: number;
}

export interface AgingReport {
  companyName: string;
  reportName: string;
  asOfDate: string;
  currency: string;
  columns: string[];
  rows: AgingRow[];
  totals: Omit<AgingRow, 'name'>;
}

export async function fetchAgingReceivable(asOfDate?: string): Promise<AgingReport> {
  return api.get<AgingReport>('/v4/aging/receivable', asOfDate ? { asOfDate } : undefined);
}

export async function fetchAgingPayable(asOfDate?: string): Promise<AgingReport> {
  return api.get<AgingReport>('/v4/aging/payable', asOfDate ? { asOfDate } : undefined);
}

export interface CustomerInvoice {
  id: string;
  docNumber: string;
  customerName: string;
  invoiceDate: string;
  dueDate: string;
  total: number;
  balance: number;
  projectName: string | null;
  memo: string | null;
  quickbooksLink: string | null;
}

export interface CustomerInvoicesResponse {
  customerName: string;
  asOfDate: string;
  invoices: CustomerInvoice[];
}

export interface InvoiceDetailLine {
  lineNum: number;
  description: string;
  quantity: number | null;
  unitPrice: number | null;
  amount: number;
  itemName: string | null;
  taxCode: string | null;
}

export interface InvoiceDetail extends CustomerInvoice {
  poNumber: string | null;
  paymentTerms: string | null;
  subtotal: number;
  taxTotal: number;
  lines: InvoiceDetailLine[];
}

export async function fetchCustomerInvoices(
  customerName: string,
  asOfDate: string,
): Promise<CustomerInvoicesResponse> {
  return api.get<CustomerInvoicesResponse>('/v4/aging/receivable/invoices', {
    customerName,
    asOfDate,
  });
}

export async function fetchInvoiceDetail(id: string): Promise<InvoiceDetail> {
  return api.get<InvoiceDetail>(`/v4/aging/receivable/invoices/${encodeURIComponent(id)}`);
}

// ---------------------------------------------------------------------------
// AR Reconcile — backfill report (MVP)
// ---------------------------------------------------------------------------

export interface ArBackfillRow {
  gmail_message_id: string;
  invoice_id: string;
  doc_number: string;
  outcome: string;
  verification_notes: string | null;
  retry_count: number;
  attempted_at: string | null;
  observed_by: string | null;
  observed_at: string | null;
  qb_reconciled: boolean;
  qb_reconciled_at: string | null;
  qb_payment_id: string | null;
  customer_company_id: string | null;
  qb_customer_id: string | null;
  customer_name: string | null;
  dms_document_id: string | null;
  qb_attachment_id: string | null;
}

export interface ArBackfillGroupedCustomer {
  customerCompanyId: string | null;
  qbCustomerId: string | null;
  customerName: string | null;
  evidenceCount: number;
  unreconciledCount: number;
  distinctInvoiceCount: number;
}

export interface ArBackfillReport {
  filters: {
    onlyUnreconciled: boolean;
    observedBy: string | null;
    customerId: string | null;
  };
  totals: {
    rowCount: number;
    unreconciledCount: number;
    customerCount: number;
  };
  groupedByCustomer: ArBackfillGroupedCustomer[];
  rows: ArBackfillRow[];
}

export async function fetchArBackfillReport(
  params: { observedBy?: string; customerId?: string; onlyUnreconciled?: boolean } = {},
): Promise<ArBackfillReport> {
  return api.get<ArBackfillReport>('/v4/acc-ar-reconcile/backfill-report', {
    observedBy: params.observedBy,
    customerId: params.customerId,
    onlyUnreconciled:
      params.onlyUnreconciled === undefined ? undefined : String(params.onlyUnreconciled),
  });
}
